import React, { useState } from "react";
import Button from "@mui/material/Button";
import Table from "@material-ui/core/Table";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import SectionPaper from "../../../common/SectionPaper";
import PropTypes from "prop-types";
import { getImageSource } from "../../api/routes";
import { Select, MenuItem } from "@material-ui/core";
import Image from "../../../common/Image";
import { useHistory, useLocation } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import IconButton from '@mui/material/IconButton';
import LaunchIcon from '@mui/icons-material/Launch';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import dayjs from "dayjs";
import OnboardingPopup from './OnboardingPopup';
import BulkUpdateDialog from './BulkUpdateDialog';
import { createClusterAndProject, saveSampleData, updateSettings, bulkUpdateContacts, bulkDeleteContacts } from '../../api/actions/mutation';
import { messageShow } from "../../../common/api/actions";
import { useDispatch, useSelector } from "react-redux";
import { formatAssigneeName } from "../../../../setup/helpers";
import { Send, Settings, Visibility as VisibilityIcon } from "@mui/icons-material";


const getStatusBadgeStyle = (status) => {
  switch (status) {
    case "Customer":
      return {
        backgroundColor: "#f0fdf4",
        color: "#16a34a",
        borderColor: "#bbf7d0",
      };
    case "Prospect":
      return {
        backgroundColor: "#fff7ed",
        color: "#ea580c",
        borderColor: "#fed7aa",
      };
    case "Lead":
      return {
        backgroundColor: "#f0f9ff",
        color: "#0284c7",
        borderColor: "#bae6fd",
      };
    case "Contact":
    default:
      return {
        backgroundColor: "#f8fafc",
        color: "#0d3559",
        borderColor: "#cbd5e1",
      };
  }
};

const Contacts = ({
  contacts,
  onDelete,
  handleLeadStatusChange,
  tab,
  permissions,
  allPermissions,
  clientConfigPermissions,
  activeTab,
  filters,
  onSendClick,
  onRefresh,
}) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const [page, setPage] = useState(pageParam);

  React.useEffect(() => {
    setPage(pageParam);
  }, [pageParam]);
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settings?.settings);
  const isStoreSubscribed = settings?.isStoreSubscribed;
  const rowsPerPage = 10;
  const [onboardingPopupOpen, setOnboardingPopupOpen] = useState(false);
  const [currentTargetContactId, setCurrentTargetContactId] = useState(null);
  const [onboardingDetails, setOnboardingDetails] = useState({
    mongoUser: "",
    mongoPassword: "",
    destinationUri: "",
  });
  const [sampleData, setSampleData] = useState(null);

  // Bulk actions state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  const handleSendClick = (contactId) => {
    onSendClick(contactId);
  };
  const handleChangePage = (event, value) => {
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("page", value);
    history.replace({ search: currentParams.toString() });
    setPage(value);
  };

  const applyFilters = () => {
    let filteredContacts = contacts || [];
    if (filters?.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredContacts = filteredContacts.filter(
        (contact) =>
          contact.storeName?.toLowerCase().includes(searchLower) ||
          contact.contactName?.toLowerCase().includes(searchLower) ||
          contact.phoneNumber?.toLowerCase().includes(searchLower) ||
          contact.email?.toLowerCase().includes(searchLower) ||
          contact.assignedTo?.toLowerCase().includes(searchLower)
      );
    }
    if (filters?.customerType) {
      const typeLower = filters.customerType.toLowerCase().trim();
      filteredContacts = filteredContacts.filter(
        (contact) => contact.customerType?.toLowerCase().includes(typeLower)
      );
    }
    if (filters?.industryType) {
      const indLower = filters.industryType.toLowerCase().trim();
      filteredContacts = filteredContacts.filter(
        (contact) => contact.industryType?.toLowerCase().includes(indLower)
      );
    }
    if (filters?.tags && filters.tags.length > 0) {
      filteredContacts = filteredContacts.filter((contact) => {
        const contactTags = (contact.tags || []).map((t) => t.toLowerCase().trim());
        return filters.tags.some((ft) => contactTags.includes(ft.toLowerCase().trim()));
      });
    }
    if (filters?.startDate) {
      const start = dayjs(filters.startDate).startOf("day");
      filteredContacts = filteredContacts.filter((contact) => {
        const d = dayjs(contact.createdDate || contact.createdAt);
        return d.isValid() && (d.isAfter(start) || d.isSame(start));
      });
    }
    if (filters?.endDate) {
      const end = dayjs(filters.endDate).endOf("day");
      filteredContacts = filteredContacts.filter((contact) => {
        const d = dayjs(contact.createdDate || contact.createdAt);
        return d.isValid() && (d.isBefore(end) || d.isSame(end));
      });
    }
    return filteredContacts;
  };

  const [dateSortOrder, setDateSortOrder] = useState(null);

  const handleSortDateJoined = () => {
    setDateSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setPage(1);
  };

  const filteredContacts = applyFilters();
  const sortedContacts = React.useMemo(() => {
    if (!dateSortOrder) return filteredContacts;
    return [...filteredContacts].sort((a, b) => {
      const dateA = a.createdDate || a.createdAt;
      const dateB = b.createdDate || b.createdAt;
      const timeA = dateA && dayjs(dateA).isValid() ? dayjs(dateA).valueOf() : 0;
      const timeB = dateB && dayjs(dateB).isValid() ? dayjs(dateB).valueOf() : 0;
      if (!timeA && !timeB) return 0;
      if (!timeA) return 1;
      if (!timeB) return -1;
      return dateSortOrder === "asc" ? timeA - timeB : timeB - timeA;
    });
  }, [filteredContacts, dateSortOrder]);

  const totalPages = Math.ceil(sortedContacts.length / rowsPerPage);
  const displayedContacts = sortedContacts.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const targetProspect = contacts?.find((c) => c._id === currentTargetContactId);

  // Selection handlers
  const displayedContactIds = displayedContacts.map((c) => c._id);
  const isAllPageSelected =
    displayedContactIds.length > 0 &&
    displayedContactIds.every((id) => selectedIds.includes(id));
  const isSomePageSelected =
    displayedContactIds.some((id) => selectedIds.includes(id)) && !isAllPageSelected;

  const handleSelectAllPage = (e) => {
    if (e.target.checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...displayedContactIds])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !displayedContactIds.includes(id)));
    }
  };

  const handleToggleSelect = (contactId) => {
    setSelectedIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  const handleBulkUpdate = async (updates, tagAction) => {
    try {
      setIsBulkSubmitting(true);
      const response = await bulkUpdateContacts({
        contactIds: selectedIds,
        updates,
        tagAction,
      });
      if (response?.data?.success) {
        dispatch(messageShow(response.data.message || "Contacts updated successfully."));
        setSelectedIds([]);
        setBulkUpdateOpen(false);
        if (onRefresh) onRefresh();
      } else {
        dispatch(messageShow(response?.data?.message || "Failed to update contacts."));
      }
    } catch (error) {
      console.error("Bulk update failed:", error);
      dispatch(messageShow(error.response?.data?.message || "Error updating contacts."));
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsBulkSubmitting(true);
      const response = await bulkDeleteContacts({
        contactIds: selectedIds,
      });
      if (response?.data?.success) {
        dispatch(messageShow(response.data.message || "Contacts deleted successfully."));
        setSelectedIds([]);
        setBulkDeleteOpen(false);
        if (onRefresh) onRefresh();
      } else {
        dispatch(messageShow(response?.data?.message || "Failed to delete contacts."));
      }
    } catch (error) {
      console.error("Bulk delete failed:", error);
      dispatch(messageShow(error.response?.data?.message || "Error deleting contacts."));
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const handleOnboardingClick = async (contactId) => {
    const contact = contacts.find((c) => c._id === contactId);
    if (!contact.industryType || !contact.subscribedPlan || !contact.email) {
      let errorMessage = "Missing required fields: ";
      if (!contact.industryType)  errorMessage += "Industry Type, "
      if (!contact.subscribedPlan)  errorMessage += "Subscribed Plan, "
      if (!contact.email) errorMessage += "Email, "
      errorMessage = errorMessage.slice(0, -2);
      dispatch(messageShow(errorMessage));
      return;
    }

    setCurrentTargetContactId(contactId);
    setOnboardingPopupOpen(true);
    if (contact?.databaseConfiguration?.uri) {
      setOnboardingDetails({ 
        mongoUser: "", 
        mongoPassword: "", 
        destinationUri: contact.databaseConfiguration.uri,
        projectId: contact.databaseConfiguration.projectId
      });
      setSampleData(null);
      return;
    }
  };

  const handleCloseOnboardingPopup = () => {
    setOnboardingPopupOpen(false);
    setCurrentTargetContactId(null);
    setOnboardingDetails({
      mongoUser: "",
      mongoPassword: "",
      destinationUri: "",
    });
    setSampleData(null);
  };

  const handleOnboardingSubmit = async (data) => {
    try {
      const response = await createClusterAndProject(data);
      setOnboardingDetails({
        mongoUser: response.data.mongoUser,
        mongoPassword: response.data.mongoPassword,
        destinationUri: response.data.destinationUri,
        projectId: response.data.projectId,
      });
      setSampleData(response.data.sampleData);
      dispatch(messageShow(response.data.message))
    } catch (error) {
      dispatch(messageShow(error.message));
      console.error("Onboarding failed:", error.response || error.message || error);
    }
  };

  const handleSaveSampleData = async (data) => {
    try {
      data.projectId = onboardingDetails.projectId;
      const response = await saveSampleData(data);
      dispatch(messageShow(response.data.message));
      return response;
    } catch (error) {
      dispatch(messageShow(error.message));
      console.error("Saving sample data failed:", error.response || error.message || error);
      throw error;
    }
  };

  const handleUpdateSettings = async (data) => {
    try {
      const response = await updateSettings({
        destinationUri: onboardingDetails.destinationUri,
        settings: data.settings,
        storeName: data.storeName
      });
      return response.data || response; // Return the complete response data
    } catch (error) {
      dispatch(messageShow(error.message));
      console.error("Updating settings failed:", error.response || error.message || error);
      // Return error information in a consistent format
      return {
        errorName: error.name,
        error: error.message,
        success: false
      };
    }
  };

  const headerCellStyle = {
    fontWeight: 700,
    fontSize: "11.5px",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "12px 14px",
    borderBottom: "2px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    whiteSpace: "nowrap",
  };

  const bodyCellStyle = {
    fontSize: "13px",
    color: "#1e293b",
    padding: "10px 14px",
    borderBottom: "1px solid #f1f5f9",
  };

  return (
    <div>
      {selectedIds.length > 0 && (
        <div
          style={{
            marginBottom: "12px",
            padding: "10px 16px",
            borderRadius: "8px",
            backgroundColor: "#eff6ff",
            border: "1px solid #bfdbfe",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontWeight: 600, fontSize: "13px", color: "#1e40af" }}>
              {selectedIds.length} contact{selectedIds.length > 1 ? "s" : ""} selected
            </span>
            <Button
              size="small"
              onClick={() => setSelectedIds([])}
              style={{ fontSize: "11.5px", textTransform: "none", color: "#64748b" }}
            >
              Deselect All
            </Button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setBulkUpdateOpen(true)}
              disabled={!permissions.canEdit}
              style={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                backgroundColor: "#0d3559",
                color: "#fff",
              }}
            >
              Bulk Update
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setBulkDeleteOpen(true)}
              disabled={!permissions.canDelete}
              style={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              Bulk Delete
            </Button>
          </div>
        </div>
      )}

      <SectionPaper style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={48} align="center" style={headerCellStyle}>
                <Checkbox
                  size="small"
                  checked={isAllPageSelected}
                  indeterminate={isSomePageSelected}
                  onChange={handleSelectAllPage}
                  disabled={displayedContacts.length === 0}
                />
              </TableCell>
              <TableCell width={200} style={headerCellStyle}>Name</TableCell>
              <TableCell width={130} style={headerCellStyle}>Phone</TableCell>
              <TableCell width={130} align="center" style={headerCellStyle}>Customer Type</TableCell>
              <TableCell width={130} align="center" style={headerCellStyle}>Lead Status</TableCell>
              <TableCell
                width={140}
                align="center"
                style={{
                  ...headerCellStyle,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={handleSortDateJoined}
              >
                <Tooltip
                  title={
                    dateSortOrder
                      ? dateSortOrder === "desc"
                        ? "Sorted: Newest to Oldest (Click to sort Oldest)"
                        : "Sorted: Oldest to Newest (Click to sort Newest)"
                      : "Click to sort by Date Joined"
                  }
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Date Joined</span>
                    <span
                      style={{
                        color: "#007BFF",
                        fontSize: "10px",
                        lineHeight: 1,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      {dateSortOrder === "asc" ? "▲" : "▼"}
                    </span>
                  </div>
                </Tooltip>
              </TableCell>
              <TableCell width={150} style={headerCellStyle}>Assigned To</TableCell>
              <TableCell width={130} align="center" style={headerCellStyle}>Lead Source</TableCell>
              <TableCell width={120} align="center" style={headerCellStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>
          {sortedContacts.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={9} align="center" style={{ padding: "48px 16px" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        backgroundColor: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#94a3b8",
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: "22px" }} />
                    </div>
                    <span style={{ fontWeight: 700, fontSize: "14px", color: "#334155" }}>
                      No {tab || "Contact"} Records Found
                    </span>
                    <span style={{ fontSize: "12.5px", color: "#64748b" }}>
                      No contacts match your current search or filter criteria.
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {displayedContacts.map((contact, index) => {
                const statusStyle = getStatusBadgeStyle(contact.leadStatus);
                const isEven = index % 2 === 0;
                const isSelected = selectedIds.includes(contact._id);
                return (
                  <TableRow
                    key={contact._id}
                    hover
                    selected={isSelected}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#f0f7ff" : isEven ? "#ffffff" : "#fcfdfe",
                      transition: "background-color 0.15s ease",
                    }}
                    onClick={() => {
                      const pathTo = (tab || "contacts").toLowerCase();
                      history.push(`/crm/view/${pathTo}/${contact._id}`, {
                        contactData: contact,
                        permissions: allPermissions,
                        activeTab: tab,
                      });
                    }}
                  >
                    <TableCell align="center" style={bodyCellStyle} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(contact._id)}
                      />
                    </TableCell>
                    <TableCell style={{ ...bodyCellStyle, color: "#0d3559" }}>
                      <div style={{ fontWeight: 600, fontSize: "13.5px" }}>
                        {isStoreSubscribed ? (contact.storeName || contact.contactName || "-") : (contact.contactName || contact.storeName || "-")}
                      </div>
                      {contact.email && (
                        <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                          <span>{contact.email}</span>
                          {contact.emailSubscription === false && (
                            <span style={{ fontSize: "10px", fontWeight: 700, backgroundColor: "#fee2e2", color: "#dc2626", padding: "1px 5px", borderRadius: "4px" }}>
                              UNSUBSCRIBED
                            </span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell style={bodyCellStyle}>
                      {contact.phoneNumber || "-"}
                    </TableCell>
                    <TableCell align="center" style={bodyCellStyle}>
                      {contact.customerType ? (
                        <span
                          style={{
                            fontSize: "11.5px",
                            fontWeight: 600,
                            padding: "3px 8px",
                            borderRadius: "6px",
                            backgroundColor: "#f1f5f9",
                            color: "#475569",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          {contact.customerType}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center" style={bodyCellStyle} onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={contact.leadStatus || "Contact"}
                        onChange={(e) =>
                          handleLeadStatusChange(contact._id, e.target.value)
                        }
                        disableUnderline
                        disabled={!permissions.canEdit}
                        style={{
                          fontSize: "11.5px",
                          fontWeight: 700,
                          borderRadius: "16px",
                          padding: "2px 10px",
                          border: `1px solid ${statusStyle.borderColor}`,
                          backgroundColor: statusStyle.backgroundColor,
                          color: statusStyle.color,
                          cursor: permissions.canEdit ? "pointer" : "default",
                        }}
                      >
                        <MenuItem value="Contact">Contact</MenuItem>
                        <MenuItem value="Lead">Lead</MenuItem>
                        <MenuItem value="Prospect">Prospect</MenuItem>
                        <MenuItem value="Customer">Customer</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center" style={{ ...bodyCellStyle, fontSize: "12px", color: "#64748b", whiteSpace: "nowrap" }}>
                      {contact.createdDate || contact.createdAt
                        ? dayjs(contact.createdDate || contact.createdAt).format("DD MMM YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell style={{ ...bodyCellStyle, fontSize: "12.5px" }}>
                      {formatAssigneeName(contact.assignedTo) || "-"}
                    </TableCell>
                    <TableCell align="center" style={bodyCellStyle}>
                      {contact.leadSource ? (
                        <span
                          style={{
                            fontSize: "11.5px",
                            fontWeight: 500,
                            padding: "3px 8px",
                            borderRadius: "6px",
                            backgroundColor: "#eef2ff",
                            color: "#4338ca",
                            border: "1px solid #c7d2fe",
                          }}
                        >
                          {contact.leadSource}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center" style={bodyCellStyle} onClick={(e) => e.stopPropagation()}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {/* <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "6px",
                              color: "#0d3559",
                              "&:hover": { backgroundColor: "#0d355912" },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const pathTo = (tab || "contacts").toLowerCase();
                              history.push(`/crm/view/${pathTo}/${contact._id}`, {
                                contactData: contact,
                                permissions: allPermissions,
                                activeTab: tab,
                              });
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        </Tooltip> */}
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "6px",
                                color: "#475569",
                                "&:hover": { backgroundColor: "#f1f5f9" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                let pathTo = tab.toLowerCase();
                                history.push(`/crm/edit/${pathTo}/${contact._id}`, {
                                  contactData: contact,
                                  permissions: allPermissions,
                                  activeTab: tab,
                                  availableTags: Array.from(new Set((contacts || []).flatMap((c) => c.tags || []))),
                                });
                              }}
                              disabled={!permissions.canEdit}
                            >
                              <EditIcon style={{ fontSize: "16px" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Send Email">
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "6px",
                                color: "#ea580c",
                                "&:hover": { backgroundColor: "#ea580c14" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendClick(contact._id);
                              }}
                              disabled={!permissions.canEdit}
                            >
                              <Send sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "6px",
                                color: "#dc2626",
                                "&:hover": { backgroundColor: "#fef2f2" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(contact._id);
                              }}
                              disabled={!permissions.canDelete}
                            >
                              <DeleteIcon style={{ fontSize: "16px" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {tab === "Prospect" && (
                          <Tooltip title="Onboarding">
                            <span>
                              <IconButton
                                size="small"
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "6px",
                                  color: "#8b5cf6",
                                  "&:hover": { backgroundColor: "#8b5cf614" },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOnboardingClick(contact._id);
                                }}
                                disabled={!permissions.canEdit}
                              >
                                <LaunchIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {tab === "Customer" && contact.clientConfigId && (
                          <Tooltip title="Client Configuration">
                            <span>
                              <IconButton
                                size="small"
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "6px",
                                  color: "#0d3559",
                                  "&:hover": { backgroundColor: "#0d355912" },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  history.push("/crm/createOrUpdate", {
                                    _id: contact.clientConfigId,
                                  });
                                }}
                                disabled={!clientConfigPermissions?.canEdit}
                              >
                                <Settings sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>

        <div style={{ padding: "14px 0", display: "flex", justifyContent: "center", borderTop: "1px solid #f1f5f9" }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            size="small"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#0d3559",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#092540",
                },
              },
            }}
          />
        </div>
      </SectionPaper>

      <Dialog
        open={bulkDeleteOpen}
        onClose={() => !isBulkSubmitting && setBulkDeleteOpen(false)}
      >
        <DialogTitle style={{ fontWeight: 700, fontSize: "16px" }}>
          Confirm Bulk Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: "13px", color: "#475569" }}>
            Are you sure you want to delete {selectedIds.length} selected contact{selectedIds.length > 1 ? "s" : ""}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button
            onClick={() => setBulkDeleteOpen(false)}
            disabled={isBulkSubmitting}
            style={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={isBulkSubmitting}
            style={{ textTransform: "none" }}
          >
            {isBulkSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <BulkUpdateDialog
        open={bulkUpdateOpen}
        onClose={() => setBulkUpdateOpen(false)}
        onBulkUpdate={handleBulkUpdate}
        selectedCount={selectedIds.length}
        isSubmitting={isBulkSubmitting}
        availableTags={Array.from(new Set((contacts || []).flatMap((c) => c.tags || [])))}
      />

      {tab === "Prospect" && targetProspect && (
        <OnboardingPopup
          open={onboardingPopupOpen}
          onClose={handleCloseOnboardingPopup}
          targetProspect={targetProspect}
          onSubmit={handleOnboardingSubmit}
          mongoUser={onboardingDetails.mongoUser}
          mongoPassword={onboardingDetails.mongoPassword}
          destinationUri={onboardingDetails.destinationUri}
          onSaveSampleData={handleSaveSampleData}
          onUpdateSettings={handleUpdateSettings}
          sampleData={sampleData}
        />
      )}
    </div>
  );
};

Contacts.propTypes = {
  contacts: PropTypes.array.isRequired,
  onDelete: PropTypes.func.isRequired,
  handleLeadStatusChange: PropTypes.func.isRequired,
  tab: PropTypes.string.isRequired,
  permissions: PropTypes.object.isRequired,
  allPermissions: PropTypes.object.isRequired,
  activeTab: PropTypes.string,
  filters: PropTypes.object,
  onRefresh: PropTypes.func,
};

export default Contacts;